import {
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';

export const serializeSortModel = (sortModel: GridSortModel | undefined) => {
  return sortModel?.length
    ? sortModel.map((sort) => `${sort.field}:${sort.sort || 'desc'}`).join(',')
    : undefined;
};

export const deserializeSortModel = (searchParams: URLSearchParams): GridSortModel | undefined => {
  const sortCriteria = searchParams.get('sort') || '';
  return sortCriteria
    ? sortCriteria.split(',').map((sort) => {
        const [field, sortDirection] = sort.split(':');
        return { field, sort: sortDirection as 'asc' | 'desc' };
      })
    : undefined;
};

// MUI's GridFilterInputDate stores a Date object (parsed from the yyyy-MM-dd
// input string, i.e. UTC midnight) — Date.toString() contains parens and a
// timezone, which breaks the `field:operator(value)` wire format. Opt-in
// (DataGrid's serializeDateFilterValues) serializes it back to the ISO date the
// input produced; existing consumers keep the legacy stringification.
const serializeFilterValue = (value: unknown, serializeDateValues: boolean) =>
  serializeDateValues && value instanceof Date ? value.toISOString().slice(0, 10) : value;

// Range operators (date `between`) hold [start, end] arrays; a half-filled
// range isn't a filter yet, and an inverted one (start > end — ISO strings, so
// lexicographic == chronological) is a user error the input surfaces — neither
// goes on the wire. Only enforced for opted-in grids so legacy consumers'
// array-valued items (if any) serialize exactly as before.
const isCompleteFilterValue = (value: unknown, serializeDateValues: boolean, operator: string) => {
  if (value === undefined || value === null) return false;
  if (!serializeDateValues || !Array.isArray(value)) return true;
  if (
    value.length === 0 ||
    value.some((entry) => entry === undefined || entry === null || entry === '')
  ) {
    return false;
  }
  if (operator === 'between' && value.length === 2) {
    const [start, end] = value;
    return typeof start === 'string' && typeof end === 'string' && start <= end;
  }
  return true;
};

export const serializeFilterModel = (
  filterModel: GridFilterModel | undefined,
  serializeDateValues = false
) => {
  if (!filterModel) return undefined;

  const { items, logicOperator, quickFilterValues, quickFilterLogicOperator } = filterModel;
  const filter =
    items
      .filter(
        (filter) =>
          filter.field &&
          filter.operator &&
          isCompleteFilterValue(filter.value, serializeDateValues, filter.operator)
      ) // filter out incomplete filters
      .map(
        (filter) =>
          `${filter.field}:${filter.operator}(${serializeFilterValue(
            filter.value,
            serializeDateValues
          )})`
      ) // serialize filters
      .join(',') || ''; // join filters with comma
  const quickFilter = quickFilterValues?.join(' ') || '';

  return filter || quickFilter
    ? {
        filter,
        quickFilter,
        logicOperator,
        quickFilterLogicOperator,
      }
    : undefined;
};

export const deserializeFilterModel = (
  searchParams: URLSearchParams
): GridFilterModel | undefined => {
  const filterCriteria = searchParams.get('filter') || '';
  const logicOperator = searchParams.get('logicOperator') || GridLogicOperator.And;
  const quickFilterValues = searchParams.get('quickFilterValues')?.split(' ') || [];
  const quickFilterLogicOperator =
    searchParams.get('quickFilterLogicOperator') || GridLogicOperator.And;

  return filterCriteria
    ? {
        items: filterCriteria
          ? filterCriteria.split(',').map((filter) => {
              const [field, operatorAndValue] = filter.split(':');
              const [operator, value] = operatorAndValue.split('(');
              return { field, operator, value: value.slice(0, -1) };
            })
          : [],
        logicOperator: logicOperator as GridLogicOperator,
        quickFilterValues,
        quickFilterLogicOperator: quickFilterLogicOperator as GridLogicOperator,
      }
    : undefined;
};

export const serializePagination = (paginationModel: GridPaginationModel | undefined) => {
  return paginationModel;
};

export const deserializePagination = (
  searchParams: URLSearchParams,
  initialPageSize?: number
): GridPaginationModel => {
  const page = parseInt(searchParams.get('page') || '1', 10) - 1;
  const pageSize = parseInt(
    searchParams.get('pageSize') || initialPageSize?.toString() || '10',
    10
  );
  return { page, pageSize };
};
